import React, { useState } from "react";
import styled from "styled-components";
import { ParsersPanel, RunNode } from "../../api/ParsersPanel";
import { useUserState } from "../../store/user/hooks";
import ArabicSourcesTab from "./ArabicSourcesTab";
import OverviewTab from "./OverviewTab";
import TamrurSourcesTab from "./TamrurSourcesTab";
import RejectionsTab from "./RejectionsTab";
import RunsTab from "./RunsTab";

type Tab =
  | "overview"
  | "tamrur"
  | "arabic"
  | "rejections"
  | "arabic_rejections"
  | "runs"
  | "arabic_runs";

const RUN_NODES: { key: RunNode; label: string }[] = [
  { key: "tamrur", label: "Tamrur" },
  { key: "dyncol1", label: "Dynamic Collectors 1" },
  { key: "dyncol2", label: "Dynamic Collectors 2" },
  { key: "dyncol3", label: "Dynamic Collectors 3" },
  { key: "news11", label: "News 11" },
  { key: "iplan", label: "Iplan" },
  { key: "nwl", label: "News With Login" },
  { key: "antique", label: "Antique" },
  { key: "antique_trends", label: "Antique Tenders" },
  { key: "boi", label: "Bank of Israel" },
  { key: "cbs", label: "CBS" },
];

const RunNodeSelect = styled.select`
  margin: 0 0 12px;
  padding: 6px 10px;
`;

const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "All Sources Overview" },
  { key: "tamrur", label: "Tamrur Sources Health" },
  { key: "arabic", label: "Arabic Sources Health" },
  { key: "rejections", label: "Rejections" },
  { key: "arabic_rejections", label: "Arabic Rejections" },
  { key: "runs", label: "Runs" },
  { key: "arabic_runs", label: "Arabic Runs" },
];

// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE_RE = /\x1b\[[0-9;]*[A-Za-z]/g;

const stripAnsi = (s: string): string => s.replace(ANSI_ESCAPE_RE, "");

const summarizeSnapshotOutput = (output?: string): string => {
  if (!output) return "Snapshot complete.";
  const summary = stripAnsi(output)
    .split("\n")
    .map((l) => l.trim())
    .reverse()
    .find((l) => l.toLowerCase().startsWith("done."));
  return summary
    ? `Snapshot complete - ${summary.replace(/^Done\.\s*/i, "")}`
    : "Snapshot complete.";
};

const ParsersPanelView: React.FC = () => {
  const { token } = useUserState();
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [runNode, setRunNode] = useState<RunNode>("tamrur");
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleUpdate = async () => {
    if (!token || isUpdating) return;
    setIsUpdating(true);
    setUpdateError(null);
    setUpdateSuccess(null);
    try {
      const res = await ParsersPanel.runSnapshot(token);
      setRefreshKey((k) => k + 1);
      setUpdateSuccess(summarizeSnapshotOutput(res?.output));
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        err?.message ||
        "Failed to run snapshot";
      setUpdateError(msg);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <PageTitle>Parsers Panel</PageTitle>
        <PageSub>Monitor collection health across all data sources</PageSub>
      </PageHeader>
      <UpdateContainer>
        {updateError && (
          <UpdateError>
            <span>{updateError}</span>
            <DismissButton
              type="button"
              aria-label="Dismiss error"
              onClick={() => setUpdateError(null)}
            >
              ×
            </DismissButton>
          </UpdateError>
        )}
        {!updateError && updateSuccess && (
          <UpdateSuccess>
            <span>{updateSuccess}</span>
            <DismissButton
              type="button"
              aria-label="Dismiss message"
              onClick={() => setUpdateSuccess(null)}
            >
              ×
            </DismissButton>
          </UpdateSuccess>
        )}
        <UpdateButton onClick={handleUpdate} disabled={isUpdating || !token}>
          {isUpdating ? "Updating…" : "Update"}
        </UpdateButton>
      </UpdateContainer>
      <TabBar>
        {TABS.map((t) => (
          <TabButton
            key={t.key}
            $active={activeTab === t.key}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </TabButton>
        ))}
      </TabBar>

      <TabContent key={refreshKey}>
        {activeTab === "overview" && <OverviewTab />}
        {activeTab === "tamrur" && <TamrurSourcesTab />}
        {activeTab === "arabic" && <ArabicSourcesTab />}
        {activeTab === "rejections" && <RejectionsTab node="tamrur" />}
        {activeTab === "arabic_rejections" && <RejectionsTab node="arabic" />}
        {activeTab === "runs" && (
          <>
            <RunNodeSelect
              value={runNode}
              onChange={(e) => setRunNode(e.target.value as RunNode)}
            >
              {RUN_NODES.map((n) => (
                <option key={n.key} value={n.key}>
                  {n.label}
                </option>
              ))}
            </RunNodeSelect>
            <RunsTab key={runNode} node={runNode} />
          </>
        )}
        {activeTab === "arabic_runs" && <RunsTab node="arabic" />}
      </TabContent>
    </Container>
  );
};

export default ParsersPanelView;

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 92px);
  background: #f1f8ff;
  padding: 32px 48px 60px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 24px 24px 40px;
  }
  @media (max-width: 768px) {
    padding: 20px 16px 32px;
    min-height: calc(100vh - 64px);
  }
  @media (max-width: 480px) {
    padding: 16px 12px 24px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;

  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #0058A9;
  margin: 0 0 4px;

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const PageSub = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #dde8f5;
  margin-bottom: 28px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 10px 22px;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? "700" : "400")};
  color: ${({ $active }) => ($active ? "#0058A9" : "#666")};
  background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  border: none;
  border-bottom: ${({ $active }) => ($active ? "2px solid #0058A9" : "2px solid transparent")};
  cursor: pointer;
  margin-bottom: -2px;
  border-radius: 6px 6px 0 0;
  transition: color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    color: #0058A9;
  }

  @media (max-width: 480px) {
    padding: 10px 14px;
    font-size: 13px;
  }
`;

const TabContent = styled.div`
  width: 100%;
`;

const UpdateContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    justify-content: stretch;
    flex-direction: column-reverse;
    align-items: stretch;
    margin-bottom: 12px;
  }
`;

const UpdateButton = styled.button`
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #0058A9;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #004785;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const UpdateError = styled.span`
  color: #c62828;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const UpdateSuccess = styled.span`
  color: #2e7d32;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const DismissButton = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  font-size: 18px;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;
